import React, { useState, useEffect } from 'react';
import { 
  Link2, Instagram, Twitter, Youtube, Globe, Music2, 
  Plus, Trash2, Save, ExternalLink, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const socialPlatforms = [
  { id: 'instagram', name: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/username', color: 'from-pink-500 to-purple-500' },
  { id: 'twitter', name: 'Twitter/X', icon: Twitter, placeholder: 'https://twitter.com/username', color: 'from-blue-400 to-blue-600' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@channel', color: 'from-red-500 to-red-600' },
  { id: 'tiktok', name: 'TikTok', icon: Music2, placeholder: 'https://tiktok.com/@username', color: 'from-gray-800 to-black' },
  { id: 'website', name: 'Website', icon: Globe, placeholder: 'https://yourwebsite.com', color: 'from-green-500 to-emerald-500' },
];

const SocialLinksSettings = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [socialLinks, setSocialLinks] = useState({});
  const [customLinks, setCustomLinks] = useState([]);
  const [newCustomLink, setNewCustomLink] = useState({ name: '', url: '' });

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/settings/social-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSocialLinks(response.data.social_links || {});
      setCustomLinks(response.data.custom_links || []);
    } catch (error) {
      console.error('Error fetching social links:', error);
    }
  };

  const handleSocialLinkChange = (platformId, value) => {
    setSocialLinks(prev => ({
      ...prev,
      [platformId]: value
    }));
  };

  const handleAddCustomLink = () => {
    if (!newCustomLink.name || !newCustomLink.url) {
      toast({ variant: 'destructive', title: 'Error', description: 'Please fill in both name and URL' });
      return;
    }
    setCustomLinks(prev => [...prev, { ...newCustomLink, id: Date.now() }]);
    setNewCustomLink({ name: '', url: '' });
  };

  const handleRemoveCustomLink = (id) => {
    setCustomLinks(prev => prev.filter(link => link.id !== id));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/client/settings/social-links`, {
        social_links: socialLinks,
        custom_links: customLinks
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Social links updated successfully' });
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to save social links' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-yellow-400" />
            Social Media Links
          </CardTitle>
          <CardDescription className="text-gray-400">
            Connect your social profiles to let fans find you everywhere
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {socialPlatforms.map((platform) => {
            const Icon = platform.icon;
            return (
              <div key={platform.id} className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${platform.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <Label className="text-sm text-gray-400 mb-1 block">{platform.name}</Label>
                  <Input
                    value={socialLinks[platform.id] || ''}
                    onChange={(e) => handleSocialLinkChange(platform.id, e.target.value)}
                    placeholder={platform.placeholder}
                    className="bg-gray-900 border-gray-600 text-white"
                  />
                </div>
                {socialLinks[platform.id] && (
                  <a 
                    href={socialLinks[platform.id]} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </a>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Custom Links */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-green-400" />
            Custom Links
          </CardTitle>
          <CardDescription className="text-gray-400">
            Add other links you want to share with your audience
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Existing Custom Links */}
          {customLinks.map((link) => (
            <div key={link.id} className="flex items-center gap-4 p-3 bg-gray-900/50 rounded-lg">
              <div className="flex-1">
                <p className="text-white font-medium">{link.name}</p>
                <p className="text-sm text-gray-400 truncate">{link.url}</p>
              </div>
              <Button
                onClick={() => handleRemoveCustomLink(link.id)}
                size="sm"
                variant="ghost"
                className="text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}

          {/* Add New Custom Link */}
          <div className="flex gap-3">
            <Input
              value={newCustomLink.name}
              onChange={(e) => setNewCustomLink(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Link name (e.g., Patreon)"
              className="bg-gray-900 border-gray-600 text-white flex-1"
            />
            <Input
              value={newCustomLink.url}
              onChange={(e) => setNewCustomLink(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://..."
              className="bg-gray-900 border-gray-600 text-white flex-1"
            />
            <Button onClick={handleAddCustomLink} size="sm" className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={loading}
          className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold"
        >
          {loading ? (
            <span className="flex items-center">
              <span className="animate-spin mr-2">⏳</span> Saving...
            </span>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Social Links
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SocialLinksSettings;
